import { motion } from 'framer-motion';
import { Network, Building2, Building, ChevronDown } from 'lucide-react';

const roleStyles = {
  'Facility Admin': { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-300' },
  'Facility User':  { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300' },
};

const userColors = {
  'AA': 'bg-rose-500 ring-rose-200',
  'FM': 'bg-violet-500 ring-violet-200',
  'SK': 'bg-amber-500 ring-amber-200',
  'MR': 'bg-cyan-500 ring-cyan-200',
};

const orgFacilities = [
  {
    name: 'Rashid Hospital', short: 'RH',
    users: [
      { initials: 'AA', name: 'Ahmed', role: 'Facility Admin' },
      { initials: 'FM', name: 'Fatima', role: 'Facility Admin' },
    ],
  },
  {
    name: 'Rashid Pharmacy', short: 'RP',
    users: [
      { initials: 'AA', name: 'Ahmed', role: 'Facility Admin' },
      { initials: 'FM', name: 'Fatima', role: 'Facility Admin' },
    ],
  },
  {
    name: 'Rashid Lab', short: 'RL',
    users: [
      { initials: 'AA', name: 'Ahmed', role: 'Facility User' },
    ],
  },
];

const standaloneFacs = [
  {
    name: 'ADNIC Claims', short: 'AC',
    users: [
      { initials: 'MR', name: 'Mariam', role: 'Facility Admin' },
    ],
  },
  {
    name: 'Dubai Clinic', short: 'DC',
    users: [
      { initials: 'SK', name: 'Sara', role: 'Facility Admin' },
    ],
  },
];

function Arrow({ delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, scaleY: 0 }}
      animate={{ opacity: 1, scaleY: 1 }}
      transition={{ delay, duration: 0.3 }}
      className="flex flex-col items-center origin-top"
    >
      <div className="w-[2px] h-5 bg-[#0A0A0A]/20" />
      <ChevronDown size={14} className="text-[#0A0A0A]/30 -mt-1.5" />
    </motion.div>
  );
}

function UserBadge({ initials, name, role, delay }) {
  const rs = roleStyles[role];
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay, duration: 0.3 }}
      className="flex items-center gap-2"
    >
      <div className={`w-6 h-6 rounded-full ${userColors[initials]} ring-2 flex items-center justify-center flex-shrink-0`}>
        <span className="text-white text-[7px] font-bold">{initials}</span>
      </div>
      <span className="text-[11px] font-body font-bold text-[#0A0A0A]">{name}</span>
      <span className={`text-[9px] font-body font-medium px-1.5 py-0.5 rounded border ${rs.bg} ${rs.text} ${rs.border}`}>{role}</span>
    </motion.div>
  );
}

function FacilityCard({ fac, delay, standalone }) {
  return (
    <div className="flex flex-col items-center">
      <Arrow delay={delay} />
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: delay + 0.1, duration: 0.4 }}
        className={`rounded-xl border p-3 w-full ${standalone ? 'bg-gray-50/50 border-gray-200 border-dashed' : 'bg-orange-50/50 border-orange-200'}`}
      >
        <div className="flex items-center gap-2 mb-2.5">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${standalone ? 'bg-gray-400' : 'bg-orange-400'}`}>
            <Building size={13} className="text-white" />
          </div>
          <div>
            <p className="font-heading font-bold text-[11px] text-[#0A0A0A] leading-tight">{fac.name}</p>
          </div>
        </div>
        {/* Arrow to users */}
        <div className="flex items-center gap-1.5 mb-2 ml-1">
          <div className="w-[1.5px] h-3 bg-[#0A0A0A]/15" />
          <span className="text-[8px] font-body text-[#737373] uppercase tracking-wider">assigned users</span>
        </div>
        <div className="space-y-1.5 ml-1">
          {fac.users.map((u, i) => (
            <UserBadge key={`${u.initials}-${i}`} initials={u.initials} name={u.name} role={u.role} delay={delay + 0.3 + i * 0.12} />
          ))}
        </div>
      </motion.div>
    </div>
  );
}

export default function CommunityStructureScene() {
  return (
    <div data-testid="scene-community-structure" className="flex flex-col items-center justify-start h-full px-8 pt-2 overflow-y-auto">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full max-w-5xl">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-4">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-body font-medium bg-teal-600/10 text-teal-700 mb-2">
            <Network size={14} /> Community Structure
          </span>
          <h2 className="font-heading font-bold text-3xl tracking-tight text-[#0A0A0A]">Community Structure</h2>
          <p className="mt-1 font-body text-sm text-[#737373]">Users are assigned to multiple facilities within their zone — each with a role per facility</p>
        </motion.div>

        {/* Community top */}
        <div className="flex justify-center">
          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 }}
            className="px-5 py-2.5 rounded-xl bg-amber-100 border border-amber-300 shadow font-heading font-bold text-sm text-amber-900 flex items-center gap-2">
            <Network size={15} /> Community
          </motion.div>
        </div>

        {/* Branching arrows from community to both zones */}
        <div className="relative" style={{ height: 40 }}>
          <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
            {/* Vertical line from community */}
            <motion.line x1="50%" y1="0" x2="50%" y2="12" stroke="#0A0A0A" strokeOpacity="0.2" strokeWidth="2"
              initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.5, duration: 0.2 }} />
            {/* Horizontal bar */}
            <motion.line x1="30%" y1="12" x2="80%" y2="12" stroke="#0A0A0A" strokeOpacity="0.2" strokeWidth="2"
              initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.6, duration: 0.3 }} />
            {/* Left branch down to org zone */}
            <motion.line x1="30%" y1="12" x2="30%" y2="32" stroke="#0A0A0A" strokeOpacity="0.2" strokeWidth="2"
              initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.8, duration: 0.2 }} />
            <motion.polygon points="0,-5 5,5 -5,5" fill="#0A0A0A" fillOpacity="0.2"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.95 }}
              style={{ transform: 'translate(30%, 32px)', transformOrigin: '30% 32px' }}>
            </motion.polygon>
            {/* Right branch down to standalone zone */}
            <motion.line x1="80%" y1="12" x2="80%" y2="32" stroke="#0A0A0A" strokeOpacity="0.2" strokeWidth="2"
              initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.85, duration: 0.2 }} />
          </svg>
          {/* Arrow tips using divs */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.95 }}
            className="absolute" style={{ left: '30%', bottom: 0, transform: 'translateX(-50%)' }}>
            <ChevronDown size={16} className="text-[#0A0A0A]/25" />
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.0 }}
            className="absolute" style={{ left: '80%', bottom: 0, transform: 'translateX(-50%)' }}>
            <ChevronDown size={16} className="text-[#0A0A0A]/25" />
          </motion.div>
        </div>

        {/* Two zones */}
        <div className="grid grid-cols-5 gap-4">
          {/* Organization zone */}
          <motion.div
            initial={{ opacity: 0, x: -15 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="col-span-3 bg-white rounded-2xl border border-orange-200 shadow-xl overflow-hidden"
          >
            <div className="flex items-center gap-2 px-4 py-3 bg-orange-50 border-b border-orange-100">
              <Building2 size={15} className="text-orange-500" />
              <span className="font-heading font-bold text-[13px] text-orange-800">Al Noor Healthcare Group</span>
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-600 font-body font-medium border border-orange-200">Organization</span>
            </div>
            <div className="grid grid-cols-3 gap-3 p-4">
              {orgFacilities.map((f, i) => (
                <FacilityCard key={f.short} fac={f} delay={0.8 + i * 0.25} />
              ))}
            </div>
            {/* Callout for org users */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.0 }}
              className="mx-4 mb-3 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-rose-500 ring-2 ring-rose-200 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-[6px] font-bold">AA</span>
                </div>
                <p className="text-[10px] font-body text-[#0A0A0A]">
                  <span className="font-bold">Ahmed</span> is in <span className="font-bold">3 facilities</span> — Facility Admin at RH, Facility Admin at RP, Facility User at RL
                </p>
              </div>
            </motion.div>
          </motion.div>

          {/* Standalone zone */}
          <motion.div
            initial={{ opacity: 0, x: 15 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7 }}
            className="col-span-2 bg-white rounded-2xl border border-dashed border-gray-300 shadow-xl overflow-hidden"
          >
            <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100">
              <Building size={15} className="text-gray-400" />
              <span className="font-heading font-bold text-[13px] text-gray-700">Standalone</span>
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-body font-medium border border-dashed border-gray-300">No org</span>
            </div>
            <div className="grid grid-cols-2 gap-3 p-4">
              {standaloneFacs.map((f, i) => (
                <FacilityCard key={f.short} fac={f} delay={1.2 + i * 0.25} standalone />
              ))}
            </div>
          </motion.div>
        </div>

        {/* Explainer — how organisation vs standalone works */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2.8, duration: 0.45 }}
          className="mt-4 mx-4 rounded-2xl border-2 border-teal-600/30 bg-gradient-to-br from-teal-50 to-white p-4 shadow-sm"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-xs font-bold">i</span>
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-heading font-bold text-[14px] text-[#0A0A0A] mb-1.5">How the community is structured</h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11.5px] font-body text-[#374151] leading-relaxed">
                <p className="flex gap-1.5">
                  <span className="text-teal-600 font-bold">1.</span>
                  <span><span className="font-semibold text-teal-700">Organisation</span> — a parent group (e.g. <span className="px-1 py-0.5 rounded bg-white border border-teal-200 text-teal-700 text-[10px] font-medium">Al Noor</span>) that owns multiple linked facilities. A user picks the org first, then is linked to one or more of its facilities.</span>
                </p>
                <p className="flex gap-1.5">
                  <span className="text-amber-600 font-bold">2.</span>
                  <span><span className="font-semibold text-amber-700">Standalone facility</span> — not part of any organisation (e.g. <span className="px-1 py-0.5 rounded bg-white border border-amber-200 text-amber-700 text-[10px] font-medium">ADNIC Claims</span>). The user is linked directly to that single facility.</span>
                </p>
                <p className="flex gap-1.5">
                  <span className="text-indigo-600 font-bold">3.</span>
                  <span><span className="font-semibold text-indigo-700">Multi-facility users</span> — one user can belong to several facilities. Example: <span className="font-medium">Ahmed</span> is in 3 facilities of Al Noor with a different role in each.</span>
                </p>
                <p className="flex gap-1.5">
                  <span className="text-gray-600 font-bold">4.</span>
                  <span><span className="font-semibold text-gray-700">Role per facility</span> — only <span className="px-1 rounded bg-teal-100 text-teal-700 text-[10px] font-medium">Facility Admin</span> or <span className="px-1 rounded bg-gray-100 text-gray-600 text-[10px] font-medium">Facility User</span>. A user can be Admin in one facility and User in another.</span>
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Legend */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3.2 }}
          className="flex items-center justify-center gap-5 mt-3 mb-3 flex-wrap">
          <span className="text-[11px] font-body font-bold text-[#0A0A0A]">Roles:</span>
          {Object.entries(roleStyles).map(([role, s]) => (
            <div key={role} className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded ${s.bg} border ${s.border}`} />
              <span className="text-[11px] font-body text-[#737373]">{role}</span>
            </div>
          ))}
          <span className="text-[11px] font-body text-[#737373]">|</span>
          <span className="text-[11px] font-body text-[#737373]">Same avatar color = same person across facilities</span>
        </motion.div>
      </motion.div>
    </div>
  );
}
